import { useState, useEffect, useRef, useCallback } from 'react'
import { useAudioContext } from 'contexts/AudioContext'

interface UseSimpleAudioControllerProps {
  audioUrl: string
  audioId?: string
  duration?: number
  onPlaybackComplete?: () => void
  onError?: (error: Error) => void
}

export const useSimpleAudioController = ({
  audioUrl,
  audioId,
  duration: metadataDuration,
  onPlaybackComplete,
  onError,
}: UseSimpleAudioControllerProps) => {
  const { currentlyPlayingAudioId, setCurrentlyPlayingAudio } =
    useAudioContext()
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [isPaused, setIsPaused] = useState(false)
  const [duration, setDuration] = useState(metadataDuration || 0)
  const [currentTime, setCurrentTime] = useState(0)
  const [isLoaded, setIsLoaded] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const cleanupAudioInstance = useCallback(() => {
    const audio = audioRef.current
    if (!audio) return

    audio.pause()
    audio.removeEventListener('loadedmetadata', () => {})
    audio.removeEventListener('timeupdate', () => {})
    audio.removeEventListener('ended', () => {})
    audio.removeEventListener('error', () => {})
    audio.removeAttribute('src')
    audio.load()
    audioRef.current = null
  }, [])

  const createAudioInstance = useCallback(() => {
    if (!audioUrl) {
      setIsLoaded(false)
      return
    }

    // Clean up existing instance
    cleanupAudioInstance()

    const audio = new Audio()
    audio.preload = 'metadata'

    audio.crossOrigin = 'anonymous'

    const handleLoadedMetadata = () => {
      setDuration(audio.duration || metadataDuration || 0)
      setIsLoaded(true)
      setError(null)
    }

    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime)
    }

    const handleEnded = () => {
      setIsPlaying(false)
      setIsPaused(false)
      setCurrentTime(0)
      setCurrentlyPlayingAudio(undefined)
      onPlaybackComplete?.()
    }

    const handleError = (event: Event) => {
      const target = event.target as HTMLAudioElement
      let errorMessage = 'Audio playback failed'

      if (target.error) {
        switch (target.error.code) {
          case MediaError.MEDIA_ERR_NETWORK:
            errorMessage = 'Network error while loading audio'
            break
          case MediaError.MEDIA_ERR_DECODE:
            errorMessage = 'Audio file could not be decoded'
            break
          case MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED:
            errorMessage = 'Audio format not supported'
            break
          default:
            errorMessage = target.error.message || 'Unknown audio error'
        }
      }

      setError(errorMessage)
      setIsLoaded(false)
      onError?.(new Error(errorMessage))
    }

    audio.addEventListener('loadedmetadata', handleLoadedMetadata)
    audio.addEventListener('timeupdate', handleTimeUpdate)
    audio.addEventListener('ended', handleEnded)
    audio.addEventListener('error', handleError)

    audio.src = audioUrl
    audio.load()
    audioRef.current = audio
  }, [
    audioUrl,
    metadataDuration,
    onPlaybackComplete,
    onError,
    setCurrentlyPlayingAudio,
    cleanupAudioInstance,
  ])

  useEffect(() => {
    createAudioInstance()

    return () => {
      cleanupAudioInstance()
    }
  }, [createAudioInstance, cleanupAudioInstance])

  useEffect(() => {
    if (currentlyPlayingAudioId !== audioId && isPlaying) {
      const audio = audioRef.current
      if (audio) {
        audio.pause()
        setIsPlaying(false)
        setIsPaused(true)
      }
    }
  }, [currentlyPlayingAudioId, audioId, isPlaying])

  const togglePlayback = useCallback(async () => {
    if (!audioUrl) {
      console.warn('No audio URL provided')
      return
    }

    try {
      if (isPlaying) {
        // Pause current audio
        const audio = audioRef.current
        if (audio) {
          audio.pause()
        }
        setIsPlaying(false)
        setIsPaused(true)
        setCurrentlyPlayingAudio(undefined)
      } else {
        // Set this as the currently playing audio first
        setCurrentlyPlayingAudio(audioId)

        cleanupAudioInstance()

        const audio = new Audio()

        audio.crossOrigin = 'anonymous'

        const handleTimeUpdate = () => {
          setCurrentTime(audio.currentTime)
        }

        const handleEnded = () => {
          setIsPlaying(false)
          setIsPaused(false)
          setCurrentTime(0)
          setCurrentlyPlayingAudio(undefined)
          onPlaybackComplete?.()
        }

        audio.addEventListener('timeupdate', handleTimeUpdate)
        audio.addEventListener('ended', handleEnded)

        // Resume from previous position if paused, otherwise start from beginning
        if (isPaused && currentTime > 0) {
          audio.currentTime = currentTime
        } else {
          audio.currentTime = 0
          setCurrentTime(0)
        }

        audioRef.current = audio
        audio.src = audioUrl

        await audio.play()
        setIsPlaying(true)
        setIsPaused(false)
        setError(null)
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Playback failed'
      console.error('Audio playback error:', error)
      setError(errorMessage)
      setIsPlaying(false)
      setIsPaused(false)
      setCurrentlyPlayingAudio(undefined)
      onError?.(new Error(errorMessage))
    }
  }, [
    isPlaying,
    isPaused,
    currentTime,
    audioUrl,
    audioId,
    setCurrentlyPlayingAudio,
    onPlaybackComplete,
    onError,
    cleanupAudioInstance,
  ])

  const seekTo = useCallback(
    (time: number) => {
      const audio = audioRef.current
      if (!audio) return

      const clampedTime = Math.max(0, Math.min(time, duration))
      audio.currentTime = clampedTime
      setCurrentTime(clampedTime)
    },
    [duration]
  )

  const skipForward = useCallback(() => {
    const audio = audioRef.current
    if (!audio) return

    const newTime = Math.min(audio.currentTime + 15, duration)
    audio.currentTime = newTime
    setCurrentTime(newTime)
  }, [duration])

  const skipBackward = useCallback(() => {
    const audio = audioRef.current
    if (!audio) return

    const newTime = Math.max(audio.currentTime - 15, 0)
    audio.currentTime = newTime
    setCurrentTime(newTime)
  }, [])

  const formatTime = useCallback((timeInSeconds: number): string => {
    const minutes = Math.floor(timeInSeconds / 60)
    const seconds = Math.floor(timeInSeconds % 60)
    return `${minutes.toString().padStart(2, '0')}:${seconds
      .toString()
      .padStart(2, '0')}`
  }, [])

  const getProgressRatio = useCallback(() => {
    if (duration === 0) return 0
    return currentTime / duration
  }, [currentTime, duration])

  const getDisplayTime = useCallback(() => {
    if (isPlaying || isPaused || currentTime > 0) {
      return formatTime(currentTime)
    }
    return formatTime(duration)
  }, [isPlaying, isPaused, currentTime, duration, formatTime])

  return {
    isPlaying,
    isPaused,
    isLoaded,
    duration,
    currentTime,

    play: togglePlayback,
    pause: togglePlayback,
    togglePlayback,
    seekTo,
    skipForward,
    skipBackward,

    formatTime,
    getProgressRatio,
    getDisplayTime,

    loading: !isLoaded,
    error,
  }
}

export default useSimpleAudioController
